import type {
  ProspectStatus,
  ProspectTier,
  InboundStatus,
  NotificationEvent,
  OutboundEmailStatus,
  ExpenseCategory,
  ExpenseStatus,
  InteractionKind,
  OpportunitySource,
  OpportunityStage,
  QuoteStatus,
  ReviewStatus,
  TaskPriority,
  TaskStatus,
} from '@/api/types'

/**
 * #399 : l'ordre des valeurs que les filtres lisent dans l'URL. Les schémas
 * d'URL sont lus au démarrage ; ces listes y vont seules, sans les libellés de
 * `lib/labels.ts`, qui les ré-exporte pour ses autres lecteurs.
 */

/** Statuts « vivants », dans l'ordre d'avancement — sans `cancelled`. */
export const TASK_STATUS_ORDER = [
  'backlog',
  'todo',
  'in_progress',
  'in_review',
  'done',
] as const satisfies readonly TaskStatus[]

export const TASK_PRIORITY_ORDER: TaskPriority[] = ['urgent', 'high', 'medium', 'low']

/** Ordre de la file : ce qui attend une décision passe devant. */
export const REVIEW_STATUS_ORDER = [
  'proposed',
  'active',
  'refused',
  'expired',
] as const satisfies readonly ReviewStatus[]

/**
 * Étapes du pipeline, dans l'ordre où une affaire les traverse. Les deux
 * dernières sont terminales : elles closent l'affaire au lieu de la faire
 * avancer, et c'est pourquoi elles sont énumérées à part quand il s'agit de
 * proposer « où déplacer cette carte ».
 */
export const OPPORTUNITY_STAGE_ORDER = [
  'nouvelle',
  'qualifiee',
  'proposition',
  'negociation',
  'gagnee',
  'perdue',
] as const satisfies readonly OpportunityStage[]

export const OPPORTUNITY_SOURCE_ORDER = [
  'recommandation',
  'entrant',
  'prospection',
  'appel_offres',
  'autre',
] as const satisfies readonly OpportunitySource[]

/**
 * Types d'échange. L'ordre est celui de la fréquence réelle : ce qu'on note
 * le plus souvent se choisit sans chercher.
 */
export const INTERACTION_KIND_ORDER = [
  'appel',
  'courriel',
  'reunion',
  'message',
  'autre',
] as const satisfies readonly InteractionKind[]

/** Statuts d'une soumission, dans l'ordre de vie (1.37.0). */
export const QUOTE_STATUS_ORDER = [
  'draft',
  'sent',
  'accepted',
  'declined',
  'expired',
  'withdrawn',
  'superseded',
] as const satisfies readonly QuoteStatus[]

/** Statuts d'une note de frais, dans l'ordre de vie (1.38.0). */
export const EXPENSE_STATUS_ORDER = [
  'draft',
  'submitted',
  'approved',
  'rejected',
  'reimbursed',
] as const satisfies readonly ExpenseStatus[]

export const EXPENSE_CATEGORY_ORDER = [
  'software',
  'hardware',
  'telecom',
  'travel',
  'meals',
  'office',
  'services',
  'fees',
  'training',
  'other',
] as const satisfies readonly ExpenseCategory[]

/** #399 : les filtres de la page des courriels, dans l'ordre des tables de libellés (`lib/labels.ts`). */
export const OUTBOUND_EMAIL_STATUS_ORDER: OutboundEmailStatus[] = [
  'scheduled',
  'sending',
  'sent',
  'logged',
  'failed',
  'suppressed',
  'cancelled',
]

export const INBOUND_STATUS_ORDER: InboundStatus[] = ['accepted', 'rejected', 'ignored']

export const NOTIFICATION_EVENT_ORDER: NotificationEvent[] = [
  'invitation',
  'spec_section.submitted',
  'delivery.created',
  'review_comment.reply',
  'spec_section.decided',
  'review_comment.created',
  'delivery.test_case_reported',
  'delivery.verdict',
  'portal_task.created',
  'quote.sent',
  'quote.decided',
  'questionnaire.sent',
  'questionnaire.response_submitted',
  'meeting_poll.sent',
  'meeting_poll.proposed',
  'meeting_poll.responded',
  'meeting_poll.decided',
  'meeting_poll.cancelled',
  'meeting.rescheduled',
  'meeting.cancelled',
  'portal_task.state_changed',
  'qa_run.assigned',
  'qa_run.closed',
  'magic_link',
  'validation.signed',
  'booking.created',
  'booking.confirmed',
  'booking.cancelled_by_client',
  'inbound.rejected',
  'portal_document.uploaded',
  'training.published',
  'secret.shared',
  'secret_request.sent',
  'secret.provided_by_client',
  'prospect.hot',
  'stop_rule.triggered',
  'ab_test.concluded',
  'ad_flight.failed',
  'corporate.obligation_due',
  'bug.assigned',
  'bug.fixed',
  'bug.commented',
  'bug.reopened',
  'project_message.created',
  'test',
]

/** #399 : les classes et états d'un prospect, que la liste de prospection lit dans l'URL. */
export const PROSPECT_TIERS = [
  'hot',
  'warm',
  'cold',
  'disqualified',
] as const satisfies readonly ProspectTier[]

export const PROSPECT_STATUSES = [
  'new',
  'contacted',
  'meeting',
  'converted',
  'lost',
  'spam',
] as const satisfies readonly ProspectStatus[]
